import { Activity, Apple, Gauge, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RestingEnergyRequirements() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const supportPoints = [{
    title: "Nutrition planning",
    description: "Establishes a precise caloric baseline to inform structured dietary guidance, fuelling protocols, and daily nutritional balance.",
    icon: Apple
  }, {
    title: "Body-composition goals",
    description: "Informs targets by aligning caloric intake and training volume with your specific physiological burn rate rather than population averages.",
    icon: Activity
  }, {
    title: "Understanding energy requirements",
    description: "Identifies your basal metabolic expenditure so you can understand daily metabolic load, recovery demands, and metabolic efficiency.",
    icon: Gauge
  }, {
    title: "Monitoring change over time",
    description: "Provides repeatable metrics to observe metabolic adaptation following changes in training, body composition, or conditioning regimens.",
    icon: TrendingUp
  }];
  return <section id="restingenergyrequirements" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-all duration-700 ease-out transform translate-y-0">
          <div className="max-w-3xl mb-14 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
              Understand your resting energy requirements
            </h2>
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Resting Metabolic Rate testing measures the energy your body uses while at rest. This provides an individual measurement rather than relying only on population-based prediction equations.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold tracking-wide text-foreground uppercase">
              Testing may support:
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportPoints.map((item, i) => {
            const IconComponent = item.icon;
            return <Card key={item.title} data-index={i} className="bg-card border-border shadow-xs hover:shadow-sm transition-shadow duration-200 h-full flex flex-col justify-between">
                  <CardHeader className="gap-3">
                    <div className="size-10 rounded-lg bg-secondary text-foreground flex items-center justify-center">
                      <IconComponent className="size-5 text-primary" aria-hidden="true" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>

          <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground max-w-xl">
              All metabolic assessments are conducted in clinical conditions with calibrated metabolic analysis systems.
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <Button asChild size="lg">
                <Link to="/BookNowPage">
                  Book an assessment
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/VO2MaxMetabolicTestingPage">
                  Metabolic testing overview
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}